import { useMutation } from '@apollo/client';

import { getQueryParam } from '@crehana/utils';

import { useCheckoutContext } from 'Jsx/CheckoutV4/Context';
import PAYMENT_MUTATION from 'Jsx/CheckoutV4/Graphql/Mutation/Payment.payments.graphql';
import {
  PaymentMutation,
  PaymentMutationVariables,
  PaymentMutation_payment_error as PaymentErrorProps,
} from 'Jsx/CheckoutV4/Graphql/Mutation/types/PaymentMutation';
import { PaymentSourceEnum } from 'Jsx/Types/Graphql/globals.payments';

import { CheckoutTKeys } from '../i18n/language';

export type CommonPaymentVariables =
  | 'paymentGateway'
  | 'itemType'
  | 'itemId'
  | 'recaptchaToken'
  | 'couponCode'
  | 'giftcard'
  | 'membershipStartedDate'
  | 'paymentSource'
  | 'itemPromotionToken'
  | 'checkedNewsLetterTerms';

export type InitialPaymentVariables = Pick<
  PaymentMutationVariables,
  CommonPaymentVariables
>;

export class PaymentError extends Error {
  code: string | null;

  translationKey: CheckoutTKeys | null;

  constructor({ code, translationKey }: Omit<PaymentErrorProps, '__typename'>) {
    super();
    this.translationKey = translationKey as CheckoutTKeys;
    this.code = code;
  }
}

const getPaymentMethodType = (methodSource: string | undefined) => {
  if (methodSource === undefined) return '';

  if (
    methodSource === 'Stripe' ||
    methodSource === 'Paypal' ||
    methodSource === 'PayU Peru' ||
    methodSource === 'Stripe Mexico' ||
    methodSource === 'Ebanx Card Brasil' ||
    methodSource === 'Ebanx Card Argentina'
  )
    return 'Online';

  return 'Offline';
};

const useCustomPayment = () => {
  const [paymentMutation, { loading }] = useMutation<
    PaymentMutation,
    PaymentMutationVariables
  >(PAYMENT_MUTATION, {
    context: { clientName: 'payments' },
  });
  const {
    selectedOfflinePaymentMethod,
    selectedPaymentMethod,
    recaptchaToken,
    checkoutProductDetail,
    checkoutProductsList,
    productData,
  } = useCheckoutContext();

  const executePayment = async (
    variables: Partial<PaymentMutationVariables>,
  ) => {
    const initialPaymentVariables: InitialPaymentVariables = {
      recaptchaToken,
      paymentGateway: selectedPaymentMethod?.paymentGatewayId?.toString() || '',
      itemId: checkoutProductDetail?.originalId?.toString() || '',
      itemType: checkoutProductDetail?.type,
      couponCode: checkoutProductDetail?.couponCode,
      giftcard: checkoutProductDetail?.giftcard,
      membershipStartedDate: checkoutProductDetail?.membershipStartedDate,
      paymentSource: PaymentSourceEnum.CHECKOUT_VIEW,
      itemPromotionToken: productData?.callCenter?.token,
    };

    try {
      if (window.gtag && checkoutProductDetail) {
        const checkoutOption = getPaymentMethodType(
          selectedOfflinePaymentMethod?.title || selectedPaymentMethod?.title,
        );

        window.gtag('event', 'checkout_progress', {
          event_category:
            checkoutProductDetail.type === 'membership' ? 'membership' : 'payg',
          send_to: 'UA-60829200-1',
          checkout_option: checkoutOption,
          checkout_step: 1,
          value: checkoutProductDetail.totalToPay || undefined,
          items: [
            {
              id: `${checkoutProductDetail.type?.toUpperCase()}_${
                checkoutProductDetail.id
              }`,
              name: checkoutProductDetail.title || undefined,
              price: checkoutProductDetail.totalToPay || undefined,
              quantity: checkoutProductsList?.length || 1,
            },
          ],
        });
      }
    } catch (error) {
      console.error(error);
    }
    const rockstarId =
      parseInt(getQueryParam('rockstar_id') as string, 10) || undefined;

    return paymentMutation({
      variables: {
        ...initialPaymentVariables,
        ...variables,
        // @ts-expect-error
        rockstarId,
      },
    })
      .then(response => {
        const error = response.data?.payment?.error;

        if (error) {
          throw new PaymentError(error);
        }

        return response;
      })
      .catch(error => {
        if (!(error instanceof PaymentError)) {
          window.Sentry?.withScope(
            (scope: { setTag: (key: string, value: string) => void }) => {
              scope.setTag('paymentMethod', selectedPaymentMethod?.title || '');
              window.Sentry.captureException(error);
            },
          );
        }

        throw error;
      });
  };

  return { paymentMutation, executePayment, loading };
};

export default useCustomPayment;
